import { DomainNameWithSubPathForLocale, GetDomainName } from "~/helpers/UrlHelper";
import { replaceBackslashes } from "~/lib/helpers/StringHelper";
import { getMetaOgTags } from "./metaOgTags";
import { AvailabilityString } from "~/lib/helpers/availabilityStringByDateHelper";
import { useMemo } from "react";
import { OFFICE_TYPE_LABEL } from "~/components/blocks/common/constants";
import { generateOrganizationLdJson } from "~/lib/helpers/schemaHelper";
import { formatUploadDate } from "~/lib/helpers/dateFormatHelper";

export function ListingMeta(data: any, path: string) {
    const { listing } = data;
    const regionConfig = data?.regionData;
    const listingAddress = `${listing.address}${listing.city ? ", " + listing.city : ""}${listing.state && listing.state !== listing.city ? ", " + listing.state : ""}${listing.zipcode ? ", " + listing.zipcode : ""}`;
    const title =
        listing.metaTitle ||
        `${listing.noOfWS ? listing.noOfWS + " Person" : ""} ${listing.spaceTypeFormula ? listing.spaceTypeFormula : ""} Office @ ${listingAddress}`;
    let description =
        listing.metaDescription ||
        `${listing.spaceTypeFormula} space for rent at ${listing.address}. Priced at $${listing?.advertisedPriceWeekly || listing.priceWeekly} weekly and perfect for a team of ${listing.noOfWS} - view for more details!`;
    const sources = ["youtu.be", "vimeo", "youtube"];
    const isValidSource = sources.some(source =>
        listing?.videoUrl?.includes(source)
    );
    description = replaceBackslashes(description);
    const baseUrl = `${DomainNameWithSubPathForLocale(regionConfig.region)}`;
    const canonicalUrl = `${GetDomainName()}`;
    let Url = canonicalUrl.endsWith("/") && path === "/" ? canonicalUrl.slice(0, -1) : canonicalUrl + (path === "/" ? "" : path);
    const countryCode = regionConfig?.region

    const firstImage = listing?.assets?.length > 0 ? listing.assets[0] : null;
    const imageUrl = firstImage?.image
        ? `${firstImage.image}`
        : "https://officehub.s3.amazonaws.com/Assets/images/1025/Serviced-Office-%28Classic%29-NSW-Sydney-1-Bligh-Street-Sydney-1Bligh-Reception-WaitingareaGallery.jpg"; // fallback image!

    const managerListArray =
        Array.isArray(listing.managersList) &&
            listing.managersList.length > 0
            ? listing.managersList.map((manager: any) => ({ ...manager }))
            : [];

    const nearbyAndTransport = [
        listing.transport,
        listing.nearby
    ]
        .filter(data => data && Array.isArray(data))
        .flatMap(data =>
            data.flatMap((item: any) =>
                item.locations.map(
                    ({ name, travelTimeFromProperty, distanceFromProperty }: any) => ({
                        type: item.type,
                        name,
                        travelTimeFromProperty,
                        distanceFromProperty
                    })
                )
            )
        );
    const hasNearbyOrTransportLocations =
        (listing.nearby && Array.isArray(listing.nearby) && listing.nearby.some(
            (item: any) => item.locations && item.locations.length > 0
        )) ||
        (listing.transport && Array.isArray(listing.transport) && listing.transport.some(
            (item: any) => item.locations && item.locations.length > 0
        ));
    const spaceAvailability =
        OFFICE_TYPE_LABEL[listing.spaceTypeFormula] ||
        listing.spaceTypeFormula;

    const availabilityString = useMemo(
        () =>
            AvailabilityString({
                status: listing?.portalSpaceStatus,
                onOffMarket: listing?.onOffMarket,
                availabilityDate: listing?.availabilityDate
            }),
        [listing]
    );
    let videoSchema = [
        {
            "script:ld+json": {
                "@context": "https://schema.org",
                "@type": "VideoObject",
                "name": title,
                "description": description,
                "thumbnailUrl": listing?.assets && listing.assets.length > 0 && listing.assets[0].image
                    ? `https://ik.imagekit.io/officehub/tr:w-1200,h-628,c-maintain_ratio,f-auto/${listing.assets[0].image}`
                    : "",
                "uploadDate": formatUploadDate(listing.mohLastModifiedDate),
                "contentUrl": isValidSource ? listing.videoUrl : "",
                "embedUrl": isValidSource ? listing.videoUrl : "",
            }
        }
    ]
    let ldJson = [
        {
            "script:ld+json": {
                "@context": "https://schema.org",
                "@type": "RealEstateListing",
                "name": title,
                "description": description || "",
                "url": baseUrl || "",
                "telephone": regionConfig?.phoneNumber || "",
                "image": imageUrl,
                address: {
                    "@type": "PostalAddress",
                    "addressCountry": countryCode,
                    "addressLocality": listing?.city || "",
                    "addressRegion": listing?.state || "",
                    "streetAddress": listing?.addressFormula || listing?.address || "",
                    "postalCode": listing?.zipcode || ""
                },
                "additionalType": listing.propertyType || "",
                "spatialCoverage": {
                    "@type": "Place",
                    "address": {
                        "@type": "PostalAddress",
                        "addressCountry": regionConfig.region || "",
                        "addressLocality": listing.city || "",
                        "addressRegion": listing.state || "",
                        "streetAddress": listing.addressFormula || "",
                        "postalCode": listing.zipcode || ""
                    },
                    "geo": {
                        "@type": "GeoCoordinates",
                        "latitude": listing.geolocationLat || "",
                        "longitude": listing.geolocationLong || ""
                    }
                },
                ...(managerListArray.length > 0 && {
                    "provider": {
                        "@type": "Organization",
                        "contactPoint": managerListArray.map((manager: any) => ({
                            "@type": "ContactPoint",
                            "name": manager.name || "",
                            "telephone": regionConfig?.phoneNumber,
                            "contactType": "Account Manager"
                        }))
                    }
                }),
                "additionalProperty": [
                    ...listing.features.map((feature: any) => ({
                        "@type": "PropertyValue",
                        "name": feature.featureName || "",
                        "value": "true"
                    })),
                    ...listing.propertyFeatures.map((feature: any) => ({
                        "@type": "PropertyValue",
                        "name": feature.featureName || "",
                        "value": "true"
                    }))
                ],
                ...(hasNearbyOrTransportLocations && {
                    "offers": nearbyAndTransport.map(({ name, travelTimeFromProperty, distanceFromProperty, type }: any) => ({
                        "@type": "LocationFeatureSpecification",
                        "name": name.replace(/["\\/]/g, ''),
                        "additionalType": type || "",
                        "description": type === "airport" ? distanceFromProperty : travelTimeFromProperty
                    }))
                }),
                "associatedMedia": {
                    "@type": "ImageObject",
                    "url": imageUrl
                },
                ...(description && {
                    "description": description
                }),
                ...(listing?.noOfWS > 0 && {
                    "identifier": [
                        {
                            "@type": "PropertyValue",
                            "name": "Capacity",
                            "value": `${listing.noOfWS > 1 ? "Up to " : ""}${listing.noOfWS} ${listing.noOfWS === 1 ? "person" : "people"}`
                        }
                    ]
                }),
            }
        }
    ];
    const canonicalLink = [{
        tagName: "link",
        rel: "canonical",
        href: Url
    }];

    // Get first hero image for preload (critical for LCP)
    const firstHeroImage = listing?.assets?.[0]?.image || listing?.assets?.[0]?.url || imageUrl;
    const hasMultipleImages = listing?.assets?.length > 1;

    // Use dimensions matching MobileHeroSectionSlider: 768x576 for mobile (4:3 aspect ratio)
    const heroImageUrl = firstHeroImage
        ? hasMultipleImages
            ? `https://ik.imagekit.io/officehub/tr:w-768,f-auto/${firstHeroImage}`
            : `https://ik.imagekit.io/officehub/tr:w-768,f-auto/${firstHeroImage}`
        : null;

    // Preload critical hero images for LCP optimization
    const preloadLinks = heroImageUrl ? [
        {
            tagName: "link",
            rel: "preload",
            as: "image",
            href: heroImageUrl,
            fetchpriority: "high",
        },
        // DNS prefetch for ImageKit to reduce connection time
        {
            tagName: "link",
            rel: "dns-prefetch",
            href: "https://ik.imagekit.io",
        },
    ] : [
        // Always prefetch ImageKit DNS even if no hero image
        {
            tagName: "link",
            rel: "dns-prefetch",
            href: "https://ik.imagekit.io",
        },
    ];

    return [
        ...preloadLinks,
        ...canonicalLink,
        ...getMetaOgTags({ title, description, imageUrl: imageUrl, canonicalUrl: Url }),
        ...generateOrganizationLdJson(baseUrl, description || "", imageUrl, regionConfig, listing),
        ...ldJson,
        ...(isValidSource ? videoSchema : [])
    ];
}
